import { observable, computed, action, makeObservable } from 'mobx';
import { ManagerWorkflow } from 'nomst/Stores/Decision/managerWorkflow';
import { WorkflowAction } from 'nomst/Stores/Decision/Constants/workflowAction';
import { User } from 'nomst/Stores/User/user';
import {
    getWorkflowApproveEvent,
    getWorkflowEvent
} from 'nomst/Stores/Decision/Constants/managerWorkflowEvent';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { EventTypes } from 'typings/enums';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { WorkflowReviewerType } from 'typings/enums';
import { IWorkflowEvent, IWorkflowApproveEvent, IWorkflowReviewer, ISetManagerWorkflowEvent } from 'typings/workflow';
import * as actions from 'nomst/Actions/manager';

export class ManagerWorkflowStore {
    @observable managerWorkflows: Map<string, ManagerWorkflow>;

    @observable workflowAction: WorkflowAction = WorkflowAction.None;

    @observable isOverBudget: boolean = false;

    @observable requireCommentWhenOverBudget: boolean = false;

    @observable comment: string = '';

    @observable managerSourceIds: string[];

    @observable loadedManagersSourceIds: string[];

    // employeeSourceIds of employees who are users
    @observable employeeUsers: Set<string>;

    private readonly _configuration: Configuration;

    constructor(configuration: Configuration) {
        makeObservable(this);
        this._configuration = configuration;
        this.managerSourceIds = [];
        this.loadedManagersSourceIds = [];
        this.managerWorkflows = new Map<string, ManagerWorkflow>();
    }

    @computed
    get selectedManagerWorkflows(): ManagerWorkflow[] {
        const workflows: ManagerWorkflow[] = [];
        const selectedManagerIds = new Set<string>(this.managerSourceIds);
        this.managerWorkflows.forEach((managerWorkflow, managerSourceId) => {
            if (selectedManagerIds.has(managerSourceId)) {
                workflows.push(managerWorkflow);
            }
        });

        return workflows;
    }

    isSendForReviewButtonVisible(currentUser: User): boolean {
        if (!this.selectedManagerWorkflows || !this.selectedManagerWorkflows.length) {
            return false;
        }

        for (const managerWorkflow of this.selectedManagerWorkflows) {
            const currentReviewerIsNotLastReviewer =
            managerWorkflow.currentReviewerIndex !== (managerWorkflow.reviewers.length - 1);
            const currentUserAsReviewerIndex =
                this.getCurrentUserAsReviewerIndex(managerWorkflow, currentUser);
            const userIsNotNextReviewerAndLast =
                (currentUserAsReviewerIndex === (managerWorkflow.reviewers.length - 1) &&
                managerWorkflow.currentReviewerIndex === (managerWorkflow.reviewers.length - 2)) === false;
            const userIsCurrentReviewerOrAfter =
                currentUserAsReviewerIndex >= managerWorkflow.currentReviewerIndex;

            if (!(currentReviewerIsNotLastReviewer
                && (currentUser.isHrAssistant ||
                    userIsNotNextReviewerAndLast &&
                    userIsCurrentReviewerOrAfter))) {
                return false;
            }
        }

        return true;
    }

    getManagerWorkflow(managerSourceId: string): ManagerWorkflow | undefined {
        return this.managerWorkflows.get(managerSourceId);
    }

    isCompMemoDisabled(): boolean {
        return this._configuration.teamPageViews.disableCompMemoDownload;
    }

    @action
    setManagerSourceIds(managerSourceIds: string[]): void {
        this.managerSourceIds = managerSourceIds;
    }

    @action
    async fetchManagerWorkflow(managerSourceIds: string[]): Promise<void> {
        const loaded = this.loadedManagersSourceIds.slice();
        if (loaded.length === managerSourceIds.length && loaded.every((val, i) => val === managerSourceIds[i])) {
            return;
        }

        const managerWorkflows = await dataManagerFactory.getManagerDataManager().getManagerWorkflows(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            managerSourceIds);

        if (managerWorkflows) {
            this.managerWorkflows = new Map<string, ManagerWorkflow>();
            managerWorkflows.forEach(w => this.managerWorkflows.set(w.managerSourceId, new ManagerWorkflow(w)));
        }
        this.loadedManagersSourceIds = managerSourceIds;
    }

    @action
    async setManagerWorkflow(events: ISetManagerWorkflowEvent[]): Promise<void> {
        await this.setAllManagerWorkflows(events);
    }

    @action
    invokeWorkflowSubmission(
        workflowAction: WorkflowAction,
        isOverBudget: boolean,
        requireCommentWhenOverBudget: boolean): void {
        this.workflowAction = workflowAction;
        this.isOverBudget = isOverBudget;
        this.requireCommentWhenOverBudget = requireCommentWhenOverBudget;
    }

    @action
    async proceedWorkflowSubmission(currentUser: User): Promise<void> {
        const events: ISetManagerWorkflowEvent[] = [];
        for (const managerWorkflow of this.selectedManagerWorkflows) {
            let event: Nullable<IWorkflowEvent | IWorkflowApproveEvent> = null;
            if (this.workflowAction === WorkflowAction.Approve) {
                event = getWorkflowApproveEvent(currentUser.userId, this.comment);
            } else if (this.workflowAction === WorkflowAction.SubmitReview) {
                const nextReviewerUser = this.getNextUserAsReviewer(managerWorkflow, currentUser);
                if (nextReviewerUser) {
                    event = getWorkflowEvent(
                        EventTypes.WorkflowSubmitEvent,
                        currentUser.userId,
                        currentUser.displayName ?? '',
                        nextReviewerUser.workflowRuleName,
                        nextReviewerUser.reviewerId,
                        nextReviewerUser.workflowReviewerType,
                        this.comment);
                }
            } else if (this.workflowAction === WorkflowAction.SendBack && managerWorkflow.previousReviewer) {
                event = getWorkflowEvent(
                    EventTypes.WorkflowSendBackEvent,
                    currentUser.userId,
                    currentUser.displayName ?? '',
                    managerWorkflow.previousReviewer.workflowRuleName,
                    managerWorkflow.previousReviewer.reviewerId,
                    managerWorkflow.previousReviewer.workflowReviewerType,
                    this.comment);
            }

            if (event) {
                events.push({
                    managerSourceId: managerWorkflow.managerSourceId,
                    event
                });
            }
        }
        if (events.length){
            await this.sendManagerWorkflow(events);
        }
        
        this.resetWorkflowSubmission();
        this.loadedManagersSourceIds = [];
        await this.fetchManagerWorkflow(this.managerSourceIds);
        await actions.resetManagerView();
    }

    @action
    resetWorkflowSubmission(): void {
        this.workflowAction = WorkflowAction.None;
        this.comment = '';
    }

    @action changeComment(comment: string): void {
        this.comment = comment;
    }

    isCurrentUserTeamManager(managerSourceId: string, currentUser: User): boolean {
        return currentUser.employeeSourceId === managerSourceId;
    }

    getCurrentUserAsReviewer(managerWorkflow: ManagerWorkflow, currentUser: User): Nullable<IWorkflowReviewer> {
        if (!managerWorkflow) {
            return null;
        }

        const currentUserAsReviewerIndex = this.getCurrentUserAsReviewerIndex(managerWorkflow, currentUser);
        return currentUserAsReviewerIndex > - 1 ?
            managerWorkflow.reviewers[currentUserAsReviewerIndex] :
            null;
    }

    getNextUserAsReviewer(managerWorkflow: ManagerWorkflow, currentUser: User): Nullable<IWorkflowReviewer> {
        if (!managerWorkflow) {
            return null;
        }

        let index = managerWorkflow.currentReviewerIndex;
        if (index < managerWorkflow.reviewers.length - 1) {
            index++;
            let nextReviewerUser = managerWorkflow.reviewers[index];

            while (this.userAndReviewerMatch(currentUser, nextReviewerUser)) {
                if (index >= managerWorkflow.reviewers.length - 1) {
                    break;
                }

                index++;
                nextReviewerUser = managerWorkflow.reviewers[index];
            }

            return nextReviewerUser;
        }

        return null;
    }

    getCurrentUserAsReviewerIndex(managerWorkflow: ManagerWorkflow, currentUser: User): number {
        if (!managerWorkflow) {
            return -1;
        }

        const currentReviewerId = managerWorkflow.currentReviewerIndex;
        const { reviewers } = managerWorkflow;
        const employeeIndices =
            this.getReviewerIndices(reviewers, currentUser.employeeSourceId ?? '', WorkflowReviewerType.Employee);
        const userIndices = this.getReviewerIndices(reviewers, currentUser.userId, WorkflowReviewerType.User);

        const combined = employeeIndices.concat(userIndices).sort((a, b) => a - b);
        if (!combined.length) {
            return -1;
        }

        for (const index of combined) {
            if (index > currentReviewerId) {
                return index;
            }
        }

        return combined[combined.length - 1];
    }

    getReviewerIndices = (reviewers: IWorkflowReviewer[], id: string, type: string): number[] => {
        return reviewers
            .map((reviewer, index) => ({ reviewer, index }))
            .filter(entry => entry.reviewer.reviewerId === id && entry.reviewer.workflowReviewerType === type)
            .map(entry => entry.index);
    }

    userAndReviewerMatch = (user: User, reviewer: IWorkflowReviewer): boolean => {
        return (reviewer.reviewerId === user.userId &&
            reviewer.workflowReviewerType === WorkflowReviewerType.User)
                || (reviewer.reviewerId === user.employeeSourceId &&
                    reviewer.workflowReviewerType === WorkflowReviewerType.Employee);
    }

    reviewerIsAUser(reviewer: IWorkflowReviewer): boolean {
        return reviewer.workflowReviewerType === WorkflowReviewerType.User ||
            this.employeeUsers.has(reviewer.reviewerId);
    }

    isUserInWorkflowAsHr(currentUser: User): boolean {
        let isUserInWorkflow = false;
        this.managerWorkflows.forEach((managerWorkflow) => {
            const userIndices = this.getReviewerIndices(
                managerWorkflow.reviewers,
                currentUser.userId,
                WorkflowReviewerType.User);
            if (userIndices.length > 0) {
                isUserInWorkflow = true;
            }
        });

        return isUserInWorkflow;
    }

    async setAllManagerWorkflows(events: ISetManagerWorkflowEvent[]): Promise<void> {
        if (events.length !== this.managerSourceIds.length) {
            throw new Error('The number of events doesn\'t match the number of managers.');
        }
        await this.sendManagerWorkflow(events);
    }

    async sendManagerWorkflow(events: ISetManagerWorkflowEvent[]): Promise<void> {
        await dataManagerFactory.getManagerDataManager().setManagerWorkflow(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            events);
    }

    async getReviewerNames(managerWorkflow: ManagerWorkflow): Promise<Map<string, string>> {
        const reviewers = managerWorkflow?.reviewers ?? [];
        const criteria = {
            userIds: [] as string[],
            employeeSourceIds: [] as string[]
        };

        for (const reviewer of reviewers) {
            if (!reviewer.reviewerId) {
                continue;
            }
            (reviewer.workflowReviewerType === WorkflowReviewerType.Employee ?
                criteria.employeeSourceIds :
                criteria.userIds)
                .push(reviewer.reviewerId);
        }
        const results =
            await dataManagerFactory.getEmployeeDataManager()
                .getPublicEmployees(this._configuration.masterAccountId, criteria);
        const employeeNameMap = new Map<string, string>();
        if (results) {
            for (const employee of results) {
                if (employee.userId) {
                    employeeNameMap.set(employee.userId, employee.displayName || employee.employeeSourceId);
                }
                employeeNameMap.set(employee.employeeSourceId, employee.displayName || employee.employeeSourceId);
            }
        }

        return employeeNameMap;
    }

}